import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';

import { Text, Separator, Button } from '@cuemath/comps';

import {
  get as getProfile,
  post as createProfile,
  patch as editProfile,
  invalidateItem as invalidateProfile,
} from 'actions/teacher-profile';
import { openModal } from 'actions/modal';
import { programNameToId } from 'utils';

import {
  Head,
  Footer,
  Message,
  FormWrapper,
  FieldWrapper,
  ButtonWrapper,
  NavigationButtons,
  NavButton,
  renderFormField,
  getOptions,
} from 'modules/form';

import {
  FORM_ACTIONS,
  FIELD_TYPES,
  USER_TAB,
  teacherProfileFields,
  getOnChangeMethod,
  getInputChangeMethod,
  getHeadText,
} from 'constants/form';

const getFormDefaults = profileData => {
  const formDefaults = {};

  teacherProfileFields.forEach(field => {
    formDefaults[field.name] =
      !!profileData && profileData[field.param]
        ? profileData[field.param]
        : field.default;
  });
  return formDefaults;
};

class CreateProfile extends PureComponent {
  static defaultProps = {
    teacher: null,
  };

  constructor(props) {
    super(props);
    const { formType } = this.props;
    this.state = {
      isValid: true,
      errorText: null,
      message: null,
      profilePost: store.post(),
      profilePatch: store.patch(),
      profileData: null,
      tab: USER_TAB.PROFILE,
      type: formType,
      form: getFormDefaults(),
      formFields: this.getFormFields(),
    };
    teacherProfileFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static propTypes = {
    actions: PropTypes.shape({
      getProfile: PropTypes.func.isRequired,
      createProfile: PropTypes.func.isRequired,
      editProfile: PropTypes.func.isRequired,
      invalidateProfile: PropTypes.func.isRequired,
    }).isRequired,
    teacher: PropTypes.object,
    onNavigate: PropTypes.func.isRequired,
    userConfig: PropTypes.object.isRequired,
    programs: PropTypes.array.isRequired,
  };

  getFormFields = () => {
    const { userConfig, programs } = this.props;
    return teacherProfileFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT) {
        field.choices = getOptions({ field: field.name, userConfig, programs });
      }
      return field;
    });
  };

  static getDerivedStateFromProps(props, state) {
    const { teacherProfile, teacher, isLast, actions } = props;
    const { profilePost, profilePatch, profileData } = state;
    const profileGet = store.get(teacherProfile, teacher.id);
    const newProfilePost = store.post(teacherProfile, profilePost.id);
    const newProfilePatch = store.patch(teacherProfile, profilePatch.id);
    const newState = {};

    if (profileGet && profileGet.isLoaded && !profileData && !profileGet.isStale) {
      newState.profileData = profileGet.data;
      if (profileGet.data && Object.keys(profileGet.data).length)
        newState.form = getFormDefaults(profileGet.data);
      else {
        newState.type = FORM_ACTIONS.CREATE;
        actions.invalidateProfile(teacher.id);
      }
    }
    if (newProfilePost.resolved) {
      if (newProfilePost.resolved.isCreated) {
        newState.profilePost = newProfilePost;
        newState.profileData = newProfilePost.resolved.data;
        newState.message = 'Profile details saved successfully';
        if (isLast) newState.type = FORM_ACTIONS.VIEW;
      } else if (newProfilePost.resolved.isCreatingFailed) {
        newState.profilePost = newProfilePost;
        newState.errorText = store.getError(newProfilePost.resolved).message;
      }
    }
    if (newProfilePatch.resolved) {
      if (newProfilePatch.resolved.isPatched) {
        newState.profilePatch = newProfilePatch;
        newState.profileData = newProfilePatch.resolved.data;
        newState.type = FORM_ACTIONS.VIEW;
        newState.message = 'Profile details saved successfully';
      } else if (newProfilePatch.resolved.isPatchingFailed) {
        newState.profilePatch = newProfilePatch;
        newState.errorText = store.getError(newProfilePatch.resolved).message;
      }
    }
    return newState;
  }

  componentDidMount() {
    const {
      actions,
      teacher: { id },
    } = this.props;
    const { profileData } = this.state;

    if (!profileData) actions.getProfile(id);
    else this.setState(() => ({ form: getFormDefaults(profileData) }));
  }

  componentDidUpdate() {
    const { tab: formTab, formType, onNavigate, teacher } = this.props;
    const { tab, type, message } = this.state;

    if (tab !== formTab || type !== formType)
      onNavigate({ tab, type, data: teacher, message });
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const { actions, teacher } = this.props;
    const {
      profilePost: { id },
      form,
      formFields,
    } = this.state;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (name === 'program') {
      } else if (name === 'teacher_programs') {
        let programsIds = [];
        form[name].forEach(
          program_group =>
            (programsIds = programsIds.concat(
              programNameToId(this.props.programs, program_group),
            )),
        );
        data[param] = Array.from(new Set(programsIds));
      } else if (param) data[param] = form[name];
    });
    actions.createProfile(id, data, { teacherId: teacher.id });
  };

  handlePatchAction = () => {
    const { actions, teacher } = this.props;
    const {
      profilePatch: { id },
      form,
      profileData,
      formFields,
    } = this.state;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (name === 'programs') {
        data[param] = form['teacher_programs'];
      } else if (name === 'teacher_programs' && profileData[param] !== form[name]) {
        let programsIds = profileData[param] ? profileData[param] : [];
        form[name].forEach(
          program_group =>
            (programsIds = programsIds.concat(
              programNameToId(this.props.programs, program_group),
            )),
        );
        data[param] = Array.from(new Set(programsIds));
      } else if (param && profileData[param] !== form[name]) data[param] = form[name];
    });
    if (Object.keys(data).length) actions.editProfile(id, teacher.id, data);
  };

  onSubmit = event => {
    const {
      profilePost: { current },
    } = this.state;
    const { formType } = this.props;

    event.preventDefault();
    if (formType === FORM_ACTIONS.CREATE && !(current && current.isCreating))
      this.handlePostAction();
    else if (formType === FORM_ACTIONS.EDIT) this.handlePatchAction();
  };

  nextTab = () => {
    const { tab } = this.state;
    this.props.onNavigate({ tab: tab + 1 });
  };

  prevTab = () => {
    const { tab } = this.state;
    this.props.onNavigate({ tab: tab - 1 });
  };

  renderFormHeader = () => {
    const { formType } = this.props;

    return (
      <Head>
        <Text size="xlarge" align="center">
          {getHeadText(formType, 'Teacher Profile')}
        </Text>
      </Head>
    );
  };

  renderFormFields = () => {
    const { formType } = this.props;
    const { formFields, form } = this.state;

    return (
      <FieldWrapper>
        {formFields.map(field => {
          const disabled =
            field.disabled ||
            formType === FORM_ACTIONS.VIEW ||
            (!field.editable && formType === FORM_ACTIONS.EDIT);
          const required = formType === FORM_ACTIONS.CREATE ? field.required : false;
          const placeholder = disabled ? '' : field.label;

          return renderFormField({
            ...field,
            value: form[field.name],
            onChange: this[getInputChangeMethod(field)],
            required,
            disabled,
            placeholder,
          });
        })}
      </FieldWrapper>
    );
  };

  renderFormFooter = () => {
    const { formType, isFirst } = this.props;
    const { profileData } = this.state;

    return (
      <Footer>
        <ButtonWrapper>
          <NavigationButtons>
            <NavButton
              label={
                formType === FORM_ACTIONS.EDIT ? 'Skip to Previous' : 'Go to Previous'
              }
              disabled={!profileData || isFirst}
              onClick={this.prevTab}
            />
          </NavigationButtons>
          <Button label="SAVE" disabled={formType === FORM_ACTIONS.VIEW} />
        </ButtonWrapper>
      </Footer>
    );
  };

  render() {
    const { message } = this.props;
    const { errorText } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader()}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter()}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ teacherProfile }) => ({ teacherProfile }),
  dispatch => ({
    actions: bindActionCreators(
      {
        getProfile,
        createProfile,
        editProfile,
        invalidateProfile,
        openModal,
      },
      dispatch,
    ),
  }),
)(CreateProfile);
